

import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, 
  Share2, 
  Settings, 
  MessageSquare, 
  Image, 
  Sparkles,
  ArrowLeft,
  RefreshCw,
  X,
  Maximize2,
  Minimize2
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import '../../styles/background-effects.css';


interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface VisualConfig {
  format: string;
  colors: string[];
  style: string;
  theme: string;
  size: string;
  referenceImages: File[];
}

const AIVisualGeneratorPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [configExpanded, setConfigExpanded] = useState(false);
  const [generatedVisuals, setGeneratedVisuals] = useState<string[]>([]);
  const [selectedReferenceImage, setSelectedReferenceImage] = useState<{ file: File; index: number } | null>(null);
  // Aurora background is always active

  const [visualConfig, setVisualConfig] = useState<VisualConfig>({
    format: '16:9',
    colors: ['#3B82F6', '#10B981', '#F59E0B'],
    style: 'moderne',
    theme: 'professionnel',
    size: '1920x1080',
    referenceImages: []
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add welcome message
  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: 'Bonjour ! Je suis votre assistant IA pour la création de visuels. Décrivez-moi ce que vous souhaitez créer et je vous aiderai à le réaliser. Vous pouvez aussi configurer le format, les couleurs et le style de votre visuel.',
        timestamp: new Date()
      }
    ]);
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsGenerating(true);

    try {
      // Simulate AI response
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const aiResponse: Message = {
        role: 'assistant',
        content: `J'ai bien compris votre demande : "${inputMessage}". Laissez-moi créer un visuel qui correspond à vos besoins. Je vais utiliser la configuration actuelle : format ${visualConfig.format}, style ${visualConfig.style}, thème ${visualConfig.theme}.${visualConfig.referenceImages.length > 0 ? ` J'ai également ${visualConfig.referenceImages.length} image(s) de référence pour m'inspirer du style et de l'esthétique.` : ''}`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiResponse]);

      // Simulate visual generation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const newVisual = `https://picsum.photos/800/600?random=${Date.now()}`;
      setGeneratedVisuals(prev => [newVisual, ...prev]);
      
      const generationMessage: Message = {
        role: 'assistant',
        content: 'Voici votre visuel généré ! Vous pouvez le télécharger, le partager ou me demander des modifications.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, generationMessage]);

    } catch (err) {
      console.error('Erreur lors de la génération du visuel:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const updateConfig = (key: keyof VisualConfig, value: any) => {
    setVisualConfig(prev => ({ ...prev, [key]: value }));
  };

  // Handle reference image uploads
  const handleReferenceImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newImages = Array.from(files);
      const maxImages = 5;
      const currentCount = visualConfig.referenceImages.length;
      
      if (currentCount + newImages.length > maxImages) {
        alert(`Vous ne pouvez pas ajouter plus de ${maxImages} images de référence. Supprimez d'abord quelques images existantes.`);
        return;
      }
      
      setVisualConfig(prev => ({
        ...prev,
        referenceImages: [...prev.referenceImages, ...newImages]
      }));
      
      alert(`${newImages.length} image(s) de référence ajoutée(s)`);
    }
  };

  const removeReferenceImage = (index: number) => {
    setVisualConfig(prev => ({
      ...prev,
      referenceImages: prev.referenceImages.filter((_, i) => i !== index)
    }));
  };

  const clearAllReferenceImages = () => {
    setVisualConfig(prev => ({
      ...prev,
      referenceImages: []
    }));
  };

  const toggleConfigExpansion = () => {
    setConfigExpanded(!configExpanded);
  };

  const closeConfig = () => {
    setShowConfig(false);
    setConfigExpanded(false);
  };

  // Drag and drop functionality
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-blue-400', 'bg-blue-50');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
    
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    if (files.length > 0) {
      const maxImages = 5;
      const currentCount = visualConfig.referenceImages.length;
      
      if (currentCount + files.length > maxImages) {
        alert(`Vous ne pouvez pas ajouter plus de ${maxImages} images de référence. Supprimez d'abord quelques images existantes.`);
        return;
      }
      
      setVisualConfig(prev => ({
        ...prev,
        referenceImages: [...prev.referenceImages, ...files]
      }));
      
      alert(`${files.length} image(s) de référence ajoutée(s) par glisser-déposer`);
    }
  };

  const formatOptions = ['16:9', '4:3', '1:1', '9:16', 'A4', 'A3'];
  const styleOptions = ['moderne', 'classique', 'minimaliste', 'coloré', 'sombre', 'vintage'];
  const themeOptions = ['professionnel', 'créatif', 'technologique', 'naturel', 'artistique', 'corporate'];
  const sizeOptions = ['1920x1080', '1280x720', '800x600', '1200x800', 'A4', 'A3'];

  // Aurora background is always active

  return (
    <DashboardLayout>
      {/* Aurora Background Effect - Fixed Full Height */}
      
      <div className="relative space-y-6 content-above-aurora">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.history.back()}
              icon={<ArrowLeft className="w-4 h-4" />}
            >
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Générateur de Visuels IA</h1>
              <p className="text-gray-600">Créez des visuels uniques en discutant avec l'IA</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowConfig(!showConfig)}
              icon={<Settings className="w-4 h-4" />}
            >
              Configuration
            </Button>
            <Button
              variant="primary"
              icon={<Sparkles className="w-4 h-4" />}
              onClick={() => setShowConfig(true)}
            >
              Nouveau Visuel
            </Button>
            {showConfig && configExpanded && (
              <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                <Maximize2 className="w-4 h-4" />
                <span>Configuration étendue</span>
              </div>
            )}
          </div>
        </div>



        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration Panel */}
          {showConfig && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={configExpanded ? "lg:col-span-3" : "lg:col-span-1"}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className="bg-white/90 backdrop-blur-sm rounded-lg p-6 border border-gray-200 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Configuration</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleConfigExpansion}
                      icon={configExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    >
                      {configExpanded ? 'Réduire' : 'Agrandir'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={closeConfig}
                      icon={<X className="w-4 h-4" />}
                    >
                      Fermer
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Format */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Format
                    </label>
                    <select
                      value={visualConfig.format}
                      onChange={(e) => updateConfig('format', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white/80 backdrop-blur-sm"
                    >
                      {formatOptions.map(format => (
                        <option key={format} value={format}>{format}</option>
                      ))}
                    </select>
                  </div>

                  {/* Style */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Style
                    </label>
                    <select
                      value={visualConfig.style}
                      onChange={(e) => updateConfig('style', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white/80 backdrop-blur-sm"
                    >
                      {styleOptions.map(style => (
                        <option key={style} value={style}>{style}</option>
                      ))}
                    </select>
                  </div>

                  {/* Theme */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thème
                    </label>
                    <select
                      value={visualConfig.theme}
                      onChange={(e) => updateConfig('theme', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white/80 backdrop-blur-sm"
                    >
                      {themeOptions.map(theme => (
                        <option key={theme} value={theme}>{theme}</option>
                      ))}
                    </select>
                  </div>

                  {/* Size */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Taille
                    </label>
                    <select
                      value={visualConfig.size}
                      onChange={(e) => updateConfig('size', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white/80 backdrop-blur-sm"
                    >
                      {sizeOptions.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>

                  {/* Colors */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Couleurs principales
                    </label>
                    <div className="flex gap-2">
                      {visualConfig.colors.map((color, index) => (
                        <div key={index} className="relative">
                          <input
                            type="color"
                            value={color}
                            onChange={(e) => {
                              const newColors = [...visualConfig.colors];
                              newColors[index] = e.target.value;
                              updateConfig('colors', newColors);
                            }}
                            className="w-10 h-10 rounded-lg border-2 border-gray-300 cursor-pointer"
                          />
                          <button
                            onClick={() => {
                              const newColors = visualConfig.colors.filter((_, i) => i !== index);
                              updateConfig('colors', newColors);
                            }}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {visualConfig.colors.length < 5 && (
                        <button
                          onClick={() => {
                            const newColors = [...visualConfig.colors, '#000000'];
                            updateConfig('colors', newColors);
                          }}
                          className="w-10 h-10 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 hover:border-gray-400 hover:text-gray-600"
                        >
                          +
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Reference Images */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Images de référence
                    </label>
                    <div className="space-y-3">
                      {/* Upload Button */}
                      <div className="relative">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleReferenceImageUpload}
                          className="hidden"
                          id="reference-images-upload"
                        />
                        <label
                          htmlFor="reference-images-upload"
                          className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors bg-white/80 backdrop-blur-sm"
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        >
                          <div className="flex flex-col items-center gap-2">
                            <Image className="w-6 h-6 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              Glissez des images ici ou cliquez pour sélectionner
                            </span>
                            <span className="text-xs text-gray-500">
                              PNG, JPG, JPEG jusqu'à 5 images
                            </span>
                          </div>
                        </label>
                      </div>

                      {/* Display uploaded images */}
                      {visualConfig.referenceImages.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              {visualConfig.referenceImages.length} image(s) de référence
                            </span>
                            <button
                              onClick={clearAllReferenceImages}
                              className="text-xs text-red-500 hover:text-red-700"
                            >
                              Tout supprimer
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {visualConfig.referenceImages.map((image, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={URL.createObjectURL(image)}
                                  alt={`Référence ${index + 1}`}
                                  className="w-full h-20 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => setSelectedReferenceImage({ file: image, index })}
                                />
                                <button
                                  onClick={() => removeReferenceImage(index)}
                                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ×
                                </button>
                                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                                  {image.name.length > 15 ? image.name.substring(0, 15) + '...' : image.name}
                                </div>
                                <div className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                  Cliquer pour agrandir
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Chat and Visuals */}
          {(!showConfig || !configExpanded) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className={`${showConfig ? 'lg:col-span-2' : 'lg:col-span-3'}`}
            >
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Chat Interface - ChatGPT Style */}
                <div className="bg-gray-900/90 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-700 shadow-lg">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-gray-300" />
                      <h3 className="text-lg font-semibold text-gray-100">Conversation avec l'IA</h3>
                    </div>
                    {visualConfig.referenceImages.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-blue-400 bg-blue-900/20 px-3 py-1 rounded-full border border-blue-800">
                        <Image className="w-4 h-4" />
                        <span>{visualConfig.referenceImages.length} image(s) de référence</span>
                      </div>
                    )}
                  </div>

                  {/* Messages */}
                  <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-900/80">
                    {messages.map((message, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] p-4 rounded-lg ${
                            message.role === 'user'
                              ? 'bg-gray-700 text-gray-100'
                              : 'bg-gray-800 text-gray-100'
                          }`}
                        >
                          <p className="text-sm leading-relaxed">{message.content}</p>
                          <p className={`text-xs mt-2 ${
                            message.role === 'user' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                    {isGenerating && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex justify-start"
                      >
                        <div className="bg-gray-800 p-4 rounded-lg">
                          <div className="flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 animate-spin text-blue-400" />
                            <span className="text-sm text-gray-300">Génération en cours...</span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input - ChatGPT Style */}
                  <div className="p-4 bg-gray-800/90 border-t border-gray-700">
                    <div className="relative">
                      <div className="flex items-end gap-3">
                        {/* Plus Button */}
                        <button className="h-10 w-10 grid place-items-center rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                        
                        {/* Text Input */}
                        <textarea
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Décrivez le visuel que vous souhaitez créer..."
                          className="flex-1 resize-none outline-none bg-transparent text-gray-100 placeholder-gray-400 p-3 min-h-[52px] max-h-32"
                          rows={1}
                        />
                        
                        {/* Microphone Button */}
                        <button className="h-10 w-10 grid place-items-center rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                          </svg>
                        </button>
                        
                        {/* Send Button */}
                        <button
                          onClick={handleSendMessage}
                          disabled={!inputMessage.trim() || isGenerating}
                          className={`h-10 w-10 grid place-items-center rounded-full transition-colors ${
                            !inputMessage.trim() || isGenerating
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* Helper Text */}
                      <div className="mt-2 text-xs text-gray-500 text-center">
                        Entrée pour envoyer • Maj+Entrée pour un retour à la ligne
                      </div>
                    </div>
                  </div>
                </div>

                {/* Generated Visuals - ChatGPT Style */}
                <div className="bg-gray-900/90 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-700 shadow-lg">
                  {/* Header */}
                  <div className="flex items-center gap-3 p-4 border-b border-gray-700">
                    <Image className="w-5 h-5 text-green-400" />
                    <h3 className="text-lg font-semibold text-gray-100">Visuels Générés</h3>
                  </div>

                  {/* Content */}
                  <div className="p-4 bg-gray-900/80">
                    <div className="space-y-4">
                      {generatedVisuals.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                          <Image className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                          <p className="text-gray-300">Aucun visuel généré pour le moment</p>
                          <p className="text-sm text-gray-500">Commencez par décrire ce que vous voulez créer</p>
                        </div>
                      ) : (
                        generatedVisuals.map((visual, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="relative group"
                          >
                            <img
                              src={visual}
                              alt={`Visuel généré ${index + 1}`}
                              className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity border border-gray-700"
                              onClick={() => {}}
                            />
                            <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button className="h-8 w-8 grid place-items-center rounded-full bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors border border-gray-600">
                                <Download className="w-4 h-4" />
                              </button>
                              <button className="h-8 w-8 grid place-items-center rounded-full bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors border border-gray-600">
                                <Share2 className="w-4 h-4" />
                              </button>
                            </div>
                          </motion.div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Reference Image Preview Modal */}
      {selectedReferenceImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Image de référence {selectedReferenceImage.index + 1}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedReferenceImage(null)}
                icon={<X className="w-4 h-4" />}
              >
                Fermer
              </Button>
            </div>
            <div className="p-4">
              <img
                src={URL.createObjectURL(selectedReferenceImage.file)}
                alt={`Référence ${selectedReferenceImage.index + 1}`}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
              />
              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Nom:</strong> {selectedReferenceImage.file.name}</p>
                <p><strong>Taille:</strong> {(selectedReferenceImage.file.size / 1024).toFixed(1)} KB</p>
                <p><strong>Type:</strong> {selectedReferenceImage.file.type}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AIVisualGeneratorPage;
